import { AlertCircle, Loader2, Pencil, Save, XCircle } from 'lucide-react';
import type { Booking } from '@/types/booking.types';

interface ReservationActionPanelProps {
  booking: Booking | undefined;
  isEditing: boolean;
  showCancelConfirm: boolean;
  cancelReason: string;
  noShowPending: boolean;
  updatePending: boolean;
  cancelPending: boolean;
  onCancelReasonChange: (value: string) => void;
  onShowCancelConfirm: (value: boolean) => void;
  onEditingChange: (value: boolean) => void;
  onEditArrivalChange: (value: string) => void;
  onEditDepartureChange: (value: string) => void;
  onEditSave: () => void;
  onCancelBooking: () => void;
  onNoShow: () => void;
}

export function ReservationActionPanel({
  booking,
  isEditing,
  showCancelConfirm,
  cancelReason,
  noShowPending,
  updatePending,
  cancelPending,
  onCancelReasonChange,
  onShowCancelConfirm,
  onEditingChange,
  onEditArrivalChange,
  onEditDepartureChange,
  onEditSave,
  onCancelBooking,
  onNoShow,
}: ReservationActionPanelProps) {
  if (showCancelConfirm) {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-red-400">Confirmar cancelamento?</p>
        <input
          type="text"
          placeholder="Motivo (opcional)"
          value={cancelReason}
          onChange={(event) => onCancelReasonChange(event.target.value)}
          className="w-full bg-muted/50 border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500/50"
        />
        <div className="flex gap-2">
          <button
            onClick={() => onShowCancelConfirm(false)}
            className="flex-1 py-2 rounded-xl bg-muted/50 hover:bg-muted transition-colors text-sm"
          >
            Voltar
          </button>
          <button
            onClick={onCancelBooking}
            disabled={cancelPending}
            className="flex-1 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 transition-colors text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1"
          >
            {cancelPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            Confirmar
          </button>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-muted-foreground">Editar datas</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Check-in</label>
            <input
              type="date"
              defaultValue={booking?.arrival_date}
              onChange={(event) => onEditArrivalChange(event.target.value)}
              className="w-full bg-muted/50 border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Check-out</label>
            <input
              type="date"
              defaultValue={booking?.departure_date}
              onChange={(event) => onEditDepartureChange(event.target.value)}
              className="w-full bg-muted/50 border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEditingChange(false)}
            className="flex-1 py-2.5 rounded-xl bg-muted/50 hover:bg-muted transition-colors font-medium text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={onEditSave}
            disabled={updatePending}
            className="flex-1 py-2.5 rounded-xl bg-linear-to-r from-primary/80 to-primary text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1"
          >
            {updatePending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3">
      {booking && !['CANCELLED', 'CHECKED_OUT', 'NO_SHOW'].includes(booking.status) ? (
        <>
          <button
            onClick={() => {
              onEditingChange(true);
              onEditArrivalChange('');
              onEditDepartureChange('');
            }}
            className="flex-1 py-2.5 rounded-xl bg-muted/50 hover:bg-muted transition-colors font-medium text-sm flex items-center justify-center gap-1"
          >
            <Pencil className="w-4 h-4" />
            Editar
          </button>
          {booking.status === 'CONFIRMED' && new Date() > new Date(booking.arrival_date) ? (
            <button
              onClick={onNoShow}
              disabled={noShowPending}
              className="py-2.5 px-3 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20 transition-colors text-sm font-medium flex items-center gap-1 disabled:opacity-50"
            >
              {noShowPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
              No-Show
            </button>
          ) : null}
          <button
            onClick={() => onShowCancelConfirm(true)}
            className="py-2.5 px-4 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-colors text-sm font-medium flex items-center gap-1"
          >
            <XCircle className="w-4 h-4" />
            Cancelar
          </button>
        </>
      ) : null}
    </div>
  );
}
